/*
 * Built-in symbols for the HMI: small UI icons (buttons, navigation) and
 * HVAC symbols (heat pump, pump, valve, tank, ...).
 *
 * Drawn here as inline SVG with currentColor, so they follow the theme and
 * work offline on a panel. Moving parts carry the class `hv-sym-move`; the
 * stylesheet animates them only while the parent has `hv-sym--running`, so
 * a stopped pump stands still.
 */

import type { JSX } from 'preact';

type Draw = () => JSX.Element;

const stroke = { fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' } as const;

/** UI icons, 24x24. */
export const ICONS: Record<string, Draw> = {
  plus: () => <path {...stroke} d="M12 5v14M5 12h14" />,
  minus: () => <path {...stroke} d="M5 12h14" />,
  power: () => <g {...stroke}><path d="M12 3v9" /><path d="M6.3 7.3a8 8 0 1 0 11.4 0" /></g>,
  home: () => <path {...stroke} d="M3 11 12 4l9 7M5 10v10h5v-6h4v6h5V10" />,
  flame: () => <path {...stroke} d="M12 3c1 4 5 5 5 10a5 5 0 0 1-10 0c0-3 2-4 2-7 2 1 3 2 3 4" />,
  drop: () => <path {...stroke} d="M12 3s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11Z" />,
  bolt: () => <path {...stroke} d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />,
  snow: () => <path {...stroke} d="M12 2v20M4.9 6.5l14.2 11M4.9 17.5l14.2-11M9 3.5l3 2 3-2M9 20.5l3-2 3 2" />,
  sun: () => <g {...stroke}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></g>,
  moon: () => <path {...stroke} d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" />,
  info: () => <g {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5v.5" /></g>,
  settings: () => <g {...stroke}><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z" /></g>,
  chart: () => <path {...stroke} d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  check: () => <path {...stroke} d="m5 12 5 5 9-10" />,
  close: () => <path {...stroke} d="M6 6l12 12M18 6 6 18" />,
  left: () => <path {...stroke} d="m15 5-7 7 7 7" />,
  right: () => <path {...stroke} d="m9 5 7 7-7 7" />,
  alarm: () => <g {...stroke}><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4M12 17v.5" /></g>,
  thermometer: () => <g {...stroke}><path d="M14 14.8V5a2 2 0 1 0-4 0v9.8a4 4 0 1 0 4 0Z" /><path d="M12 9v7" /></g>,
  leaf: () => <path {...stroke} d="M5 19c0-9 6-14 15-14 0 9-5 15-14 15M5 19l7-7" />,
  clock: () => <g {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></g>,
};

/** HVAC symbols, 64x64, line art. */
export const SYMBOLS: Record<string, { label: string; draw: Draw }> = {
  'heat-pump': {
    label: 'Wärmepumpe',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <rect x="6" y="8" width="52" height="48" rx="6" />
        <circle cx="24" cy="32" r="12" />
        <g class="hv-sym-move hv-sym-spin" style={{ transformOrigin: '24px 32px' }}>
          <path d="M24 32c0-6 4-9 7-8M24 32c5 3 6 8 3 10M24 32c-5 3-10 1-10-3" />
        </g>
        <path d="M42 18h8M42 26h8M42 34h8M42 42h8" />
      </g>
    ),
  },
  pump: {
    label: 'Pumpe',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <circle cx="32" cy="32" r="20" />
        <path d="M2 32h10M52 32h10" />
        <g class="hv-sym-move hv-sym-spin" style={{ transformOrigin: '32px 32px' }}>
          <path d="M32 14 46 40H18Z" />
        </g>
      </g>
    ),
  },
  fan: {
    label: 'Ventilator',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <circle cx="32" cy="32" r="26" />
        <g class="hv-sym-move hv-sym-spin" style={{ transformOrigin: '32px 32px' }}>
          <path d="M32 32c-2-10 2-18 8-18 4 6-2 14-8 18ZM32 32c10 2 16 8 14 14-7 1-12-6-14-14ZM32 32c-8 6-17 6-20 0 3-6 12-5 20 0Z" />
        </g>
        <circle cx="32" cy="32" r="3" />
      </g>
    ),
  },
  valve: {
    label: 'Ventil',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M2 40h12M50 40h12M14 28l36 24V28L14 52Z" />
        <path d="M32 40V14M24 14h16" />
      </g>
    ),
  },
  'valve-3way': {
    label: 'Dreiwegeventil',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M2 32h12M50 32h12M32 50v12M14 22v20l18-10ZM50 22v20L32 32ZM22 50h20L32 32Z" />
      </g>
    ),
  },
  tank: {
    label: 'Speicher',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <rect x="16" y="4" width="32" height="56" rx="14" />
        <path d="M22 22c4 3 8-3 12 0s8 3 10 0M22 34c4 3 8-3 12 0s8 3 10 0" class="hv-sym-move hv-sym-wave" />
      </g>
    ),
  },
  radiator: {
    label: 'Heizkörper',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M8 14v38M18 14v38M28 14v38M38 14v38M48 14v38M58 14v38M4 20h58M4 48h58" />
        <path d="M14 8c2-3 6-3 8 0M30 8c2-3 6-3 8 0" class="hv-sym-move hv-sym-rise" />
      </g>
    ),
  },
  'floor-heating': {
    label: 'Fußbodenheizung',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M4 52h56M10 44h44c4 0 4-8 0-8H14c-4 0-4-8 0-8h40c4 0 4-8 0-8H14" />
        <path d="M20 10c2-3 6-3 8 0M36 10c2-3 6-3 8 0" class="hv-sym-move hv-sym-rise" />
      </g>
    ),
  },
  sensor: {
    label: 'Fühler',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M36 38V12a4 4 0 1 0-8 0v26a10 10 0 1 0 8 0Z" />
        <path d="M32 24v20" />
      </g>
    ),
  },
  house: {
    label: 'Haus',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M6 30 32 8l26 22M12 26v30h40V26" />
        <path d="M26 56V40h12v16" />
      </g>
    ),
  },
  sun: {
    label: 'Sonne',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <circle cx="32" cy="32" r="12" />
        <g class="hv-sym-move hv-sym-spin-slow" style={{ transformOrigin: '32px 32px' }}>
          <path d="M32 4v8M32 52v8M4 32h8M52 32h8M12 12l6 6M46 46l6 6M12 52l6-6M46 18l6-6" />
        </g>
      </g>
    ),
  },
  snowflake: {
    label: 'Kühlen',
    draw: () => (
      <g {...stroke} stroke-width={2.5} class="hv-sym-move hv-sym-spin-slow" style={{ transformOrigin: '32px 32px' }}>
        <path d="M32 4v56M8 18l48 28M8 46l48-28M24 8l8 6 8-6M24 56l8-6 8 6" />
      </g>
    ),
  },
  flame: {
    label: 'Flamme',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path class="hv-sym-move hv-sym-flicker" style={{ transformOrigin: '32px 58px' }}
              d="M32 4c3 12 16 16 16 32a16 16 0 0 1-32 0c0-9 6-12 6-21 6 3 9 7 9 13" />
      </g>
    ),
  },
  brine: {
    label: 'Sole',
    // Erdreich oben, darunter drei Wellen, die nach rechts fliessen. Jede
    // Welle ist breiter gezeichnet als die Flaeche (Periode 16) und wird
    // genau um eine Periode verschoben, dadurch laeuft sie nahtlos.
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <defs>
          <clipPath id="hv-sym-brine-clip"><rect x="4" y="18" width="56" height="42" rx="6" /></clipPath>
        </defs>
        <path d="M4 10h56" />
        <path d="M10 4l-4 6M22 4l-4 6M34 4l-4 6M46 4l-4 6M58 4l-4 6" stroke-width={1.6} />
        <rect x="4" y="18" width="56" height="42" rx="6" />
        <g clip-path="url(#hv-sym-brine-clip)">
          <path class="hv-sym-move hv-sym-flowx" d="M-12 30q4-5 8 0t8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0" />
          <path class="hv-sym-move hv-sym-flowx hv-sym-flowx--slow" d="M-12 40q4-5 8 0t8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0" />
          <path class="hv-sym-move hv-sym-flowx" d="M-12 50q4-5 8 0t8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0 8 0" />
        </g>
      </g>
    ),
  },
  plug: {
    label: 'Strom',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M24 4v14M40 4v14M16 18h32v10a16 16 0 0 1-32 0ZM32 44v16" />
        <path d="M34 22l-6 10h8l-6 10" class="hv-sym-move hv-sym-blink" />
      </g>
    ),
  },
  water: {
    label: 'Warmwasser',
    draw: () => (
      <g {...stroke} stroke-width={2.5}>
        <path d="M32 6s16 18 16 30a16 16 0 0 1-32 0C16 24 32 6 32 6Z" />
        <path d="M26 40c2 4 8 6 12 2" class="hv-sym-move hv-sym-blink" />
      </g>
    ),
  },
};

export function Icon({ name, size = 24 }: { name: string; size?: number | string }) {
  const draw = ICONS[name];
  if (!draw) return null;
  return (
    <svg class="hv-icon" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">{draw()}</svg>
  );
}

export const ICON_NAMES = Object.keys(ICONS);
export const SYMBOL_NAMES = Object.keys(SYMBOLS);
